import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { useSignatureInspection } from "../../signing/useSignatureInspection";
import type { SignatureInspection } from "../../signing/signatureInspection";

type InspectionQuery = ReturnType<typeof useSignatureInspection>;
type Labels = ReturnType<typeof useLanguage>["t"]["documents"]["signedContainer"]["panel"];

function inspectionState(inspection: InspectionQuery): SignatureInspection["state"] {
  if (inspection.isPending || inspection.isRefreshing) return "pending";
  if (inspection.isError || inspection.refreshError) return "failed";
  return inspection.data?.state ?? "pending";
}

function statusMessage(
  state: SignatureInspection["state"],
  errorCode: string | null | undefined,
  labels: Labels
) {
  if (state === "unsupported") {
    return errorCode === "file_too_large"
      ? labels.inspectionTooLarge
      : labels.inspectionUnsupported;
  }
  const messages = {
    pending: labels.inspectionChecking,
    failed: labels.inspectionFailed,
    complete: labels.inspectionChecked,
  };
  return messages[state];
}

export function SignatureInspectionStatus({ inspection }: { inspection: InspectionQuery }) {
  const { t } = useLanguage();
  const labels = t.documents.signedContainer.panel;
  const result = inspection.data;
  const state = inspectionState(inspection);
  const busy = state === "pending";
  const message = statusMessage(state, result?.errorCode, labels);

  return (
    <div className="space-y-2 border-b px-5 py-3">
      <p role="status" className="flex items-center gap-2 text-xs text-muted-foreground">
        {busy && <Loader2 className="h-3 w-3 animate-spin" />}
        {message}
      </p>
      {result?.checkedAt && (
        <p className="text-xs text-muted-foreground">
          {labels.inspectionCheckedAt.replace(
            "{date}",
            new Date(result.checkedAt).toLocaleString()
          )}
        </p>
      )}
      {!busy && state !== "unsupported" && (
        <Button size="sm" variant="outline" onClick={inspection.refresh}>
          {labels.inspectionRetry}
        </Button>
      )}
    </div>
  );
}
